'use client';

import type { Webform } from '@formio/js';
import dynamic from 'next/dynamic';
import { FormSource, type FormType } from '@formio/react';
import { type ComponentType, useCallback, useMemo, useRef } from 'react';
import { normalizeFormioDateSeparatorsForForm, useFormioForm } from '@/lib/hooks/useFormioForm';
import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IForms } from '@/.generated';
import { trackAdobeFormStart, trackAdobeFormError, trackAdobeFormSubmit } from '@/utils/adobe';
import { setupAuthoredModal } from '../../../../utils/setupAuthoredModal';
import { FormioInit } from '@/components/primitives/FormioInit';

// tsgen types IForms.form as `unknown` (Contentstack's Form.io marketplace app
// stores the embedded definition as a JSON field); narrow it to the emitted shape.
type FormsProps = WithMetadata<IBaseComponentProps> &
  Omit<IForms, 'form'> & {
    form?: { form: FormType };
    getSubmissionData?: () => Record<string, unknown>;
    onSubmitStatus?: (status: 'success' | 'error') => void;
    formEventNames?: string[];
    onFormEvent?: (eventName: string, payload: unknown) => void;
  };

// Registry of complex forms keyed by form.io form name — each lazy-loaded on demand.
const complexFormRegistry: Record<string, ComponentType> = {
  mapRequest: dynamic(() => import('@/components/forms/Map Request/MapRequest'), {
    ssr: false,
    loading: () => <FormSkeleton />,
  }),
  jobTimelineLookup: dynamic(
    () => import('@/components/forms/JobTimelineLookup/job-timeline-lookup-form'),
    {
      ssr: false,
      loading: () => <FormSkeleton />,
    }
  ),
  shareholderCompensation: dynamic(
    () => import('@/components/forms/Shareholder Compensation/ShareholderCompensationForm'),
    {
      ssr: false,
      loading: () => <FormSkeleton />,
    }
  ),
};

const Form = dynamic(() => import('@formio/react').then((m) => m.Form), {
  ssr: false,
  loading: () => <FormSkeleton />,
});

function FormSkeleton() {
  return (
    <div className="max-w-150 mx-auto space-y-4 animate-pulse">
      <div className="h-10 bg-gray-200 rounded" />
      <div className="h-10 bg-gray-200 rounded" />
      <div className="h-24 bg-gray-200 rounded" />
      <div className="h-10 bg-gray-200 rounded w-32" />
    </div>
  );
}

// Renders a complex (code-built) form matched by form.io form name.
const ComplexForm = (props: FormsProps) => {
  const { form, componentName, _metadata } = props;
  const formName = form?.form?.name ?? '';
  const ComplexFormComponent = complexFormRegistry[formName] ?? null;

  if (!ComplexFormComponent) return null;

  return (
    <Container componentName={componentName} metaId={_metadata?.uid}>
      <ComplexFormComponent />
    </Container>
  );
};

// Renders a simple form.io form from the embedded JSON definition.
const SimpleForm = (props: FormsProps) => {
  const {
    form,
    componentName,
    _metadata,
    project_id,
    meeting_id,
    getSubmissionData,
    onSubmitStatus,
    formEventNames,
    onFormEvent,
    enable_additional_information,
    disable_form_bottom_spacing
  } = props;
  // Human-readable form name for analytics (e.g. "Contact Us"); falls back to the machine name
  const analyticsFormName = form?.form.title ?? form?.form.name ?? '';
  // Guards the Form Start event so it fires only on the user's first interaction
  const hasTrackedStartRef = useRef(false);

  // In production, serve the form schema embedded from Contentstack (form.form).
  // In every other environment, fetch the live definition from Form.io via
  // useFormioForm (passing a formName triggers the API call) so authors see the
  // latest draft without republishing Contentstack.
  const isProduction = process.env.NEXT_PUBLIC_CONTENTSTACK_ENVIRONMENT === 'prod';
  const { formDefinition, translations, currentLocale, isLoading } = useFormioForm({
    formName: isProduction ? undefined : form?.form?.name,
    autoPreload: true,
  });
  const renderedFormSource = useMemo<FormSource | null>(() => {
    if (isProduction) {
      if (!form?.form) return null;
      const formName = typeof form.form.name === 'string' ? form.form.name : '';
      return normalizeFormioDateSeparatorsForForm(formName, form.form as FormType);
    }
    return (formDefinition as FormSource) ?? null;
  }, [form?.form, formDefinition, isProduction]);

  const handleFormReady = useCallback(
    (instance: Webform) => {
      const wrapper = (instance.element as HTMLElement | undefined)?.closest('.formio-parent');

      // Form.io templates can render boolean values as true/false in authored
      // success messages; normalize them to user-friendly Yes/No.
      const normalizeSuccessBooleans = () => {
        requestAnimationFrame(() => {
          const successMessage = wrapper?.querySelector('.submit-success-message');
          if (!successMessage) return;

          const walker = document.createTreeWalker(successMessage, NodeFilter.SHOW_TEXT);
          let node = walker.nextNode();
          while (node) {
            const text = node.textContent;
            if (text && /\btrue\b|\bfalse\b/i.test(text)) {
              node.textContent = text
                .replace(/\btrue\b/gi, 'Yes')
                .replace(/\bfalse\b/gi, 'No');
            }
            node = walker.nextNode();
          }
        });
      };

      // Seed hidden projectId/meetingId fields from Contentstack config. Skipped
      // when unconfigured, leaving any form-defined default intact.
      const setHiddenFieldValue = (key: string, value?: string) => {
        if (!value || !enable_additional_information) return;
        instance.getComponent(key)?.setValue(value);
      };
      setHiddenFieldValue('projectId', project_id);
      setHiddenFieldValue('meetingId', meeting_id);

      const showStatus = (succeeded: boolean) => {
        wrapper?.classList.toggle('is-submit-success', succeeded);
        wrapper?.classList.toggle('is-submit-error', !succeeded);
        if (succeeded) normalizeSuccessBooleans();
        onSubmitStatus?.(succeeded ? 'success' : 'error');
      };

      const clearStatus = () => {
        wrapper?.classList.remove('is-submit-success', 'is-submit-error');
      };

      // highlightErrors is off: on a failed submit, scroll the topmost invalid
      // field (by viewport position) into view and focus it. rAF lets Form.io
      // paint the error classes first.
      const scrollToFirstError = () => {
        if (!wrapper) return;
        requestAnimationFrame(() => {
          const errored = Array.from(
            wrapper.querySelectorAll<HTMLElement>('.formio-component.has-error')
          ).filter((el) => el.offsetParent !== null);
          if (!errored.length) return;
          const topMost = errored.reduce((a, b) =>
            a.getBoundingClientRect().top <= b.getBoundingClientRect().top ? a : b
          );
          topMost.scrollIntoView({ behavior: 'smooth', block: 'center' });
          topMost
            .querySelector<HTMLElement>('input, select, textarea, [tabindex]')
            ?.focus({ preventScroll: true });
        });
      };

      const scrollToFormTop = () =>
        requestAnimationFrame(() =>
          wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        );

      // Project Siting uses a custom button action script that performs its own
      // API call and emits submitProjectSitingSuccess/submitProjectSitingError.
      // Skip the generic submit proxy to avoid overriding that status.
      const isProjectSitingCustomFlow = form?.form.name === 'projectSiting';

      // Rendered from a form object (no `src`), so Form.io never persists. Proxy
      // the submission through our API route, then emit submitDone/submitError to
      // release the button. `saved` guards against a double submit.
      const handleSubmit = (submission: Record<string, unknown>, saved: boolean) => {
        if (saved) return;
        const extraData = getSubmissionData?.();
        if (extraData) {
          submission.data = { ...(submission.data as Record<string, unknown>), ...extraData };
        }
        void (async () => {
          try {
            const res = await fetch('/api/form-submission', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: form?.form.name, submission }),
            });
            if (!res.ok) {
              const errorText = 'Submission failed. Please try again.';
              instance.emit('submitError', errorText);
              showStatus(false);
              trackAdobeFormError({ errorText });
              return;
            }
            instance.emit('submitDone', (await res.json()) ?? submission);
            showStatus(true);
            trackAdobeFormSubmit({ formName: analyticsFormName });
            scrollToFormTop();
          } catch (error) {
            console.error('[Forms] Failed to submit form:', error);
            const errorText = 'Submission failed. Please try again.';
            instance.emit('submitError', errorText);
            showStatus(false);
            trackAdobeFormError({ errorText });
          }
        })();
      };

      // Form Start: fire once on the user's first field focus
      const handleFormStart = () => {
        if (hasTrackedStartRef.current) return;
        hasTrackedStartRef.current = true;
        trackAdobeFormStart({ formName: analyticsFormName });
      };

      // Pull human-readable messages out of Form.io's validation payload, which may be a
      // string, an error object ({ message } or core's { errorKeyOrMessage }), or nested arrays.
      const collectErrorMessages = (input: unknown): string[] => {
        if (!input) return [];
        if (typeof input === 'string') return input.trim() ? [input.trim()] : [];
        if (Array.isArray(input)) return input.flatMap(collectErrorMessages);
        if (typeof input === 'object') {
          const err = input as Record<string, unknown>;
          if (typeof err.message === 'string' && err.message.trim()) return [err.message.trim()];
          if (typeof err.errorKeyOrMessage === 'string' && err.errorKeyOrMessage.trim())
            return [err.errorKeyOrMessage.trim()];
          if (err.messages) return collectErrorMessages(err.messages);
          if (err.errors) return collectErrorMessages(err.errors);
        }
        return [];
      };

      // Form Error: fire for every validation failure — required fields and format/sanity
      // checks alike. Form.io emits a falsy payload when errors clear, so ignore that.
      const handleValidationError = (errors: unknown) => {
        if (!errors) return;
        const errorText = Array.from(new Set(collectErrorMessages(errors))).join(', ');
        if (errorText) trackAdobeFormError({ errorText });
      };

      instance.on('focus', handleFormStart);
      instance.on('error', handleValidationError);
      instance.on('error', scrollToFirstError);
      instance.on('nextPage', scrollToFormTop);
      if (!isProjectSitingCustomFlow) {
        instance.on('submit', handleSubmit);
      }

      // Custom-action forms (e.g. Project Siting) can emit explicit success/error
      // events instead of going through Form.io's standard submit lifecycle.
      // Mirror those events into the same wrapper status classes used by CSS.
      instance.on('submitProjectSitingSuccess', () => {
        showStatus(true);
        scrollToFormTop();
      });
      instance.on('submitProjectSitingError', () => {
        showStatus(false);
      });
      instance.on('submitProjectSitingValidationError', () => {
        clearStatus();
      });

      formEventNames?.forEach((eventName) => {
        instance.on(eventName, (payload: unknown) => {
          onFormEvent?.(eventName, payload);
        });
      });

      // Wizard-only: Form.io renders the Previous/Next/Submit bar
      // (.formio-wizard-nav-container) as the wizard's last child, so any
      // authored component always sits above it. Relocate these tagged
      // containers to sit *below* the nav bar, preserving list order. The nav
      // container only exists in wizard display, so normal forms are untouched.
      const root = instance.element as HTMLElement | undefined;
      if (root?.querySelector('.formio-wizard-nav-container')) {
        const BELOW_NAV_CLASSES = [
          'recaptcha-content',
          'submit-success-message',
          'submit-error-message',
        ];

        // Idempotent: only moves a node when it isn't already correctly placed,
        // so it can safely run on every render and MutationObserver callback
        // without triggering a reflow loop.
        const reconcileBelowNav = () => {
          const nav = root.querySelector('.formio-wizard-nav-container');
          if (!nav) return;
          let anchor: Element = nav;
          for (const className of BELOW_NAV_CLASSES) {
            const target = root.querySelector(`.${className}`);
            if (!target) continue;
            if (target !== anchor && anchor.nextElementSibling !== target) {
              anchor.after(target);
            }
            anchor = target;
          }
        };

        let scheduled = false;
        let observer: MutationObserver | null = null;
        const teardown = () => {
          observer?.disconnect();
          observer = null;
        };
        // Coalesce bursts of events/mutations into a single rAF-timed pass, and
        // self-clean once the form is removed from the DOM.
        const schedule = () => {
          if (scheduled) return;
          scheduled = true;
          requestAnimationFrame(() => {
            scheduled = false;
            if (!root.isConnected) {
              teardown();
              return;
            }
            reconcileBelowNav();
          });
        };

        // Page navigation rebuilds the wizard DOM (resetting placement); these
        // events cover it. The observer additionally catches DOM changes that
        // emit no Form.io event, e.g. conditional success/error messages
        // toggling visibility after submit.
        instance.on('render', schedule);
        instance.on('nextPage', schedule);
        instance.on('prevPage', schedule);
        instance.on('destroy', teardown);
        observer = new MutationObserver(schedule);
        observer.observe(root, { childList: true, subtree: true });
        schedule();
      }

      // Dismissal + scroll-lock for an authored modal popup (Form.io HTML component).
      if (wrapper) setupAuthoredModal(instance, wrapper);
    },
    [
      analyticsFormName,
      form?.form.name,
      project_id,
      meeting_id,
      getSubmissionData,
      formEventNames,
      onFormEvent,
      onSubmitStatus,
      enable_additional_information,
    ]
  );

  if (!form) return null;
  if (isLoading) return <FormSkeleton />;
  if (renderedFormSource == null) return null;
  const formSource: FormSource = renderedFormSource;

  // Form.io's Calendar widget passes options.language straight through to flatpickr as
  // its locale and fetches `flatpickr-<language>.js` from Form.io's CDN. Flatpickr only
  // ships base ISO 639-1 locale files (e.g. "es"); region-qualified codes like "en-us"
  // (this project's DEFAULT_LOCALE) have no matching file and 404 on every date field.
  // Stripping the region tag keeps the i18n key and language in sync (Form.io looks up
  // translations by exact match on options.language) while giving flatpickr a code it recognizes.
  const formioLanguage = currentLocale.split('-')[0];

  return (
    <Container componentName={componentName} metaId={_metadata?.uid} bottomMargin={!disable_form_bottom_spacing}>
      <div className="w-full formio-parent">
        <Form
          src={formSource}
          onFormReady={handleFormReady}
          options={{
            language: formioLanguage,
            i18n: { [formioLanguage]: translations },
            highlightErrors: false,
            noAlerts: true,
          }}
        />
      </div>
    </Container>
  );
};

// Routes to the complex form if the form name is registered, otherwise renders form.io.
// FormioInit mounts here (not in SharedPageLayout) so formio only loads on pages with forms.
const Default = (props: FormsProps) => {
  const formName = props?.form?.form?.name ?? '';
  // Return early if no form name is provided
  if (!formName) return null;
  return (
    <>
      <FormioInit />
      {formName in complexFormRegistry ? <ComplexForm {...props} /> : <SimpleForm {...props} />}
    </>
  );
};

const variants = {
  Default,
};

export const Forms = (props: FormsProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
